<template>
    <Dialog v-model:open="isDialogVisible">
        <DialogContent data-testid="cookie-preferences-dialog">
            <DialogHeader>
                <DialogTitle>{{ $t("CookiePolicy.SettingsTitle") }}</DialogTitle>
            </DialogHeader>
            <p v-html="$t('CookiePolicy.SettingsBlocksDescription')"></p>
            <Accordion type="single" collapsible>
                <AccordionItem v-for="{ id, value, title, description } in accordionItems" :key="id" :value>
                    <AccordionTrigger>
                        <div class="flex items-center gap-3" @click.stop>
                            <Checkbox v-if="value === 'necessary'" :model-value="true" disabled />
                            <Checkbox v-else :id v-model="draft[value]" :data-testid="id" />
                            <label :for="id">{{ title }}</label>
                        </div>
                    </AccordionTrigger>
                    <AccordionContent>{{ description }}</AccordionContent>
                </AccordionItem>
            </Accordion>
            <DialogFooter class="sm:grid sm:grid-cols-3">
                <Button data-testid="cookie-preferences-accept-all" variant="primary" fill="outline" @click="acceptAll">
                    {{ $t("CookiePolicy.SettingsAcceptAll") }}
                </Button>
                <Button
                    data-testid="cookie-preferences-reject"
                    variant="primary"
                    fill="outline"
                    @click="acceptEssential">
                    {{ $t("CookiePolicy.SettingsAcceptEssential") }}
                </Button>
                <Button data-testid="cookie-preferences-save" variant="primary" fill="outline" @click="onSave">
                    {{ $t("CookiePolicy.SettingsSave") }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
<script setup lang="ts">
type DialogAccordionItem = {
    id: string;
    value: "necessary" | "analytics" | "targeting";
    title: string;
    description: string;
};

const { t } = useI18n();
const { preferences, isDialogVisible, acceptAll, acceptEssential, savePreferences } = useCookieConsent();

// Local editable draft so simply opening/closing the dialog doesn't silently persist changes.
const draft = reactive({ analytics: true, targeting: false });

const accordionItems = computed<DialogAccordionItem[]>(() => [
    {
        id: "cookie-preferences-necessary",
        value: "necessary",
        title: t("CookiePolicy.SettingsBlock1Title"),
        description: t("CookiePolicy.SettingsBlock1Description"),
    },
    {
        id: "cookie-preferences-analytics",
        value: "analytics",
        title: t("CookiePolicy.SettingsBlock2Title"),
        description: t("CookiePolicy.SettingsBlock2Description"),
    },
    {
        id: "cookie-preferences-targeting",
        value: "targeting",
        title: t("CookiePolicy.SettingsBlock3Title"),
        description: t("CookiePolicy.SettingsBlock3Description"),
    },
]);

watch(
    isDialogVisible,
    (visible) => {
        if (!visible) return;
        if (preferences.value) {
            draft.analytics = preferences.value.analytics;
            draft.targeting = preferences.value.targeting;
        }
    },
    { immediate: true }
);

function onSave() {
    savePreferences({ analytics: draft.analytics, targeting: draft.targeting });
}
</script>
